// Tiny module-level pub/sub for surfacing mutation failures to the operator.
//
// Wired to the React Query client's MutationCache.onError (see main.tsx) so
// EVERY mutation - including fire-and-forget `.mutate()` calls that never await
// or render their own error - gets a user-visible error path by default (the
// hard checklist rule "every mutation has user-visible error handling"). A
// mutation that fully owns its own error UI opts out with
// `meta: { silentError: true }`; everything else falls through to the toast.
//
// Dependency-free (no external toast lib): a small store + a <MutationErrorToast>
// subscriber rendered once at the app root.

export interface MutationErrorEntry {
  id: number;
  message: string;
}

type Listener = (entries: MutationErrorEntry[]) => void;

let entries: MutationErrorEntry[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

const emit = () => {
  for (const l of listeners) l(entries);
};

export const pushMutationError = (message: string): void => {
  entries = [...entries, { id: nextId++, message }].slice(-3); // keep the last few
  emit();
};

export const dismissMutationError = (id: number): void => {
  entries = entries.filter((e) => e.id !== id);
  emit();
};

export const subscribeMutationErrors = (listener: Listener): (() => void) => {
  listeners.add(listener);
  listener(entries);
  return () => {
    listeners.delete(listener);
  };
};

// Normalise an unknown error thrown by a mutation into a readable line.
export const mutationErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error) return error;
  return "Something went wrong. Please try again.";
};
